<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.clipboard-list-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Dashboard') }}
          </h1>
        </div>
        <div class="flex md:mt-0 md:ml-4">
          <div class="rounded-md shadow-sm md:flex">
            <div class="relative inline-flex rounded-none">
              <select @change="letsUpdate" aria-label="Sort by" class="form-input bg-white rounded-none rounded-l-md" id="payment" v-model="filters.payment">
                <option :value="null">{{ $t('By payment') }}</option>
                <option :value="1">{{ $t('paid') }}</option>
                <option :value="0">{{ $t('unpaid') }}</option>
              </select>
            </div>
            <div class="relative inline-flex rounded-none">
              <select @change="letsUpdate" aria-label="Sort by" class="form-input bg-white rounded-none" id="status" v-model="filters.status">
                <option :value="null">{{ $t('By status') }}</option>
                <template v-for="(status, index) in statusList">
                  <option :key="index" :value="status.id">
                    {{ status.body }}
                  </option>
                </template>
              </select>
            </div>
            <div class="relative inline-flex rounded-none">
              <select @change="letsUpdate" aria-label="Sort by" class="form-input bg-white rounded-none" id="status" v-model="filters.priority">
                <option :value="null">{{ $t('By priority') }}</option>
                <template v-for="(priority, index) in priorityList">
                  <option :key="index" :value="priority.id">
                    {{ priority.name }}
                  </option>
                </template>
              </select>
            </div>
            <div class="relative inline-flex rounded-none">
              <select @change="letsUpdate" aria-label="Sort by" class="form-input bg-white rounded-none rounded-r-md" id="status" v-model="filters.technician">
                <option :value="null">{{ $t('By technician') }}</option>
                <template v-for="(technician, index) in technicianList">
                  <option :key="index" :value="technician.id">
                    {{ technician.name }}
                  </option>
                </template>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 md:px-8">
      <template>
        <div class="py-4">
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">
                  {{ $t('Amount') }}
                </div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.amount | currency }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">{{ $t('Cost') }}</div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.cost | currency }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">{{ $t('Tax') }}</div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.tax | currency }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">
                  {{ $t('Profit') }}
                </div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.profit | currency }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">
                  {{ $t('Repair orders') }}
                </div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.orders }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">
                  {{ $t('Brands') }}
                </div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.brands }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">
                  {{ $t('Devices') }}
                </div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.devices }}
                </div>
              </div>
            </div>
            <div class="bg-white overflow-hidden rounded-md shadow">
              <loading :status="loading"></loading>
              <div class="px-2 py-3 sm:p-4">
                <div class="text-sm font-medium truncate uppercase">
                  {{ $t('Defects') }}
                </div>
                <div class="mt-1 font-semibold text-gray-900">
                  {{ states.defects }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full">
          <div class="grid grid-cols-1 gap-2">
            <div class="flex flex-col bg-white rounded-lg shadow">
              <div class="p-4">
                <div class="font-semibold">
                  {{ $t('Annual repair orders') }}
                </div>
              </div>
              <div class="p-4">
                <loading :status="loading"></loading>
                <line-chart :chart-data="chartData" :height="350" ref="chart"></line-chart>
              </div>
            </div>
          </div>
        </div>
      </template>
      <div class="w-full text-xs px-2 text-gray-400 mt-4">
        <strong>{{ $t('Help links') }}</strong>
        <ul class="px-2">
          <li>
            <a class="hover:text-blue-600" href="https://google.com/repair-box/faq#what-is-a-repair-boxs-workflow" target="_blank">{{
              $t("What is a repair box's workflow?")
            }}</a>
          </li>
          <li>
            <a class="hover:text-blue-600" href="https://google.com/repair-box/faq#how-to-configured-sms-sending-gateways" target="_blank">
              {{ $t('How to configured SMS sending gateways?') }}</a
            >
          </li>
          <li>
            <a class="hover:text-blue-600" href="https://google.com/repair-box/faq#how-to-configure-payment-gateways" target="_blank">
              {{ $t('How to configure payment gateways?') }}</a
            >
          </li>
          <li>
            <a class="hover:text-blue-600" href="https://google.com/repair-box/faq#i-have-already-a-website-how-can-use-the-repair-box" target="_blank">
              {{ $t('What if i have already a website how can use the repair box?') }}</a
            >
          </li>

          <li>
            <a class="hover:text-blue-600" href="https://google.com/repair-box/faq#how-to-frontend-portfolio-can-be-customized" target="_blank">
              {{ $t('How to frontend portfolio can be customized?') }}</a
            >
          </li>
        </ul>
      </div>
    </div>
  </main>
</template>
<script>
import LineChart from '@/components/elements/line-chart';

export default {
  name: 'dashboard',
  metaInfo() {
    return {
      title: this.$i18n.t('Admin dashboard'),
    };
  },
  components: { LineChart },
  data() {
    return {
      loading: false,
      statusList: [],
      priorityList: [],
      technicianList: [],
      filters: {
        status: null,
        priority: null,
        technician: null,
        payment: null,
      },
      states: {
        amount: 0,
        brands: 0,
        cost: 0,
        defects: 0,
        devices: 0,
        orders: 0,
        profit: 0,
        tax: 0,
      },
      chartData: {
        labels: [
          this.$t('Jan'),
          this.$t('Feb'),
          this.$t('Mar'),
          this.$t('Apr'),
          this.$t('May'),
          this.$t('Jun'),
          this.$t('Jul'),
          this.$t('Aug'),
          this.$t('Sept'),
          this.$t('Oct'),
          this.$t('Nov'),
          this.$t('Dec'),
        ],
        datasets: [
          {
            label: this.$i18n.t('Repair Orders'),
            backgroundColor: '#4299e1',
            data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
          },
        ],
      },
    };
  },
  mounted() {
    this.getFilters();
    this.getStates();
    this.getData();
  },
  computed: {
    anyFilter() {
      return this.filters.status !== null || this.filters.payment !== null || this.filters.priority !== null;
    },
    datasets() {
      return this.chartData.datasets[0].data;
    },
  },
  watch: {
    datasets() {
      this.$refs.chart.update();
    },
  },
  methods: {
    letsUpdate() {
      this.getStates();
      this.getData();
    },
    getFilters() {
      axios.get(route('repair-orders.filters')).then((response) => {
        this.statusList = response.data.statuses;
        this.priorityList = response.data.priorities;
        this.technicianList = response.data.technicians;
      });
    },
    getStates() {
      this.loading = true;
      axios
        .get(route('dashboard.states'), {
          params: this.filters,
        })
        .then((response) => {
          this.loading = false;
          this.states = response.data;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getData() {
      this.loading = true;
      axios
        .get(route('dashboard.annual.graph'), {
          params: this.filters,
        })
        .then((response) => {
          this.chartData.datasets[0].data = response.data;
          this.loading = false;
        });
    },
  },
};
</script>
